"use client";

import { useCallback, useId, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheckIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useRouteContext } from "@/components/custom/contexts/routeContext";
import {
  useAnonymizationStore,
  type AnonymizationThreadState,
} from "@/components/custom/stores/anonymizationStore";
import { setThreadAnonymization } from "@/lib/anonymization";
import { ClientLogger } from "@/lib/clientLogger";
import { cn } from "@/lib/utils";

/**
 * Composer anonymization toggle (Switch variant). Replaces the 2-segment
 * Plain/Anonymized ToggleGroup with a single labeled Switch — compact,
 * green-accented when ON, neutral when OFF.
 *
 * Preserves the original safety semantics:
 *  - Optimistic PATCH with rollback on failure.
 *  - Flip-off confirmation dialog when `everAnonymized` (turning OFF mid-thread
 *    means the next turn replays prior history in clear — the user must
 *    explicitly confirm). Intercept fires BEFORE Radix flips the visual state,
 *    so dialog Cancel leaves the switch in its current position.
 *  - Pre-creation `newThread` slot for the welcome screen (toggle before a
 *    thread exists — persisted onto the new thread at create time).
 *
 * The info strip ("anonymization.info.on/off") was removed by design; the
 * label + green accent + Switch position carry the meaning.
 */
export const ComposerAnonymizationSwitch: FC = () => {
  const { t } = useTranslation();
  const routeState = useRouteContext();
  const threadId = routeState.threadId;

  const threadState = useAnonymizationStore((s) =>
    threadId ? s.byThread[threadId] : undefined,
  );
  const newThreadState = useAnonymizationStore((s) => s.newThread);
  const setStoreState = useAnonymizationStore((s) => s.set);
  const setNewThread = useAnonymizationStore((s) => s.setNewThread);

  const state = threadId ? threadState : newThreadState;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pending, setPending] = useState(false);

  const switchId = useId();

  // Project default is OFF (AnonymizationOptions.DefaultEnabled = false). When the store
  // slot is empty (race on first load / brand-new thread before fetch), default the
  // visual to OFF to match what the backend will return once the slot populates.
  const enabled = state?.enabled === true;
  const everAnonymized = state?.everAnonymized === true;

  const applyChange = useCallback(
    async (next: boolean) => {
      if (!threadId) {
        setNewThread({ enabled: next, everAnonymized: false });
        return;
      }
      // Rollback target if the PATCH fails. When the store slot was empty we fall back
      // to `false` — same default the server would stamp on a fresh thread, and the safe
      // privacy choice if we genuinely don't know the prior state.
      const previous: AnonymizationThreadState = threadState ?? {
        enabled: false,
        everAnonymized,
      };
      setStoreState(threadId, { enabled: next, everAnonymized });
      setPending(true);
      try {
        const fresh = await setThreadAnonymization(threadId, next);
        setStoreState(threadId, fresh);
      } catch (err) {
        ClientLogger.warn("Failed to update anonymization toggle (switch)", {
          action: `PATCH /threads/${threadId}/anonymization`,
          component: "ComposerAnonymizationSwitch",
          error: err instanceof Error ? err : undefined,
        });
        setStoreState(threadId, previous);
      } finally {
        setPending(false);
      }
    },
    [threadId, threadState, everAnonymized, setStoreState, setNewThread],
  );

  // Intercept BEFORE Radix flips the visual state — the confirmation dialog
  // must fire first so Cancel leaves the switch in its current position.
  const onCheckedChange = useCallback(
    (next: boolean) => {
      if (next === enabled) return;
      if (!next && everAnonymized) {
        setConfirmOpen(true);
        return;
      }
      void applyChange(next);
    },
    [enabled, everAnonymized, applyChange],
  );

  return (
    <>
      <div
        className={cn(
          "flex items-center gap-2",
          pending && "opacity-70 cursor-wait",
        )}
      >
        <ShieldCheckIcon
          className={cn(
            "size-[18px] shrink-0 transition-colors",
            enabled ? "text-green-600" : "text-muted-foreground",
          )}
          aria-hidden="true"
        />
        <label
          htmlFor={switchId}
          className={cn(
            "text-sm transition-colors cursor-pointer select-none",
            enabled ? "text-foreground" : "text-muted-foreground",
          )}
        >
          {t("anonymization.toggle.label")}
        </label>
        <Switch
          id={switchId}
          checked={enabled}
          onCheckedChange={onCheckedChange}
          disabled={pending}
          aria-label={t("anonymization.toggle.label")}
          className="data-[state=checked]:bg-green-600"
        />
      </div>

      <Dialog
        open={confirmOpen}
        onOpenChange={(o) => {
          if (!pending) setConfirmOpen(o);
        }}
      >
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheckIcon className="size-5 text-amber-500" />
              {t("anonymization.flipOff.title")}
            </DialogTitle>
            <DialogDescription>
              {t("anonymization.flipOff.message")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={pending}
            >
              {t("anonymization.flipOff.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                await applyChange(false);
                setConfirmOpen(false);
              }}
              disabled={pending}
            >
              {t("anonymization.flipOff.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
